import { useState } from 'react';
import { toast } from 'react-toastify';
import { productoService } from '../../services/productoService';

interface ImportProductosModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const ImportProductosModal = ({ onClose, onSuccess }: ImportProductosModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [createCategories, setCreateCategories] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preview, setPreview] = useState<null | { created: number; updated: number; errors: Array<{ row: number; message: string }> }>(null);

  const handleDownloadTemplate = async () => {
    try {
      const header = 'sku,name,description,categoriaId,categoriaName,tipo,precioBase,unidadMedida,manejaInventario,costoPromedio,costoEstandar,codigoBarras,imagenUrl,isActive\n';
      const example = 'SKU-001,Producto de ejemplo,Desc ejemplo,,Categoria Ejemplo,producto,100.00,unidad,true,80.00,90.00,7500000000010,,true\n';
      const blob = new Blob([header + example], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_productos.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('No se pudo descargar la plantilla');
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Selecciona un archivo CSV');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await productoService.importCsv(file, { dryRun, createCategories });
      setPreview(res.data);
      if (!dryRun) {
        toast.success(`Importación completada: creados ${res.data.created}, actualizados ${res.data.updated}`);
        onSuccess();
        onClose();
      } else {
        toast.info('Simulación completada. Revisa los resultados y ejecuta sin simulación.');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Error al importar CSV');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Importar Productos</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">×</button>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={handleDownloadTemplate} className="px-3 py-1 border rounded hover:bg-gray-50">Descargar plantilla</button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Archivo CSV</label>
            <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <p className="text-xs text-gray-500 mt-1">Columnas: sku,name,description,categoriaId,categoriaName,tipo,precioBase,unidadMedida,manejaInventario,costoPromedio,costoEstandar,codigoBarras,imagenUrl,isActive</p>
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              <span>Simulación (dry-run)</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked={createCategories} onChange={(e) => setCreateCategories(e.target.checked)} />
              <span>Crear categorías si no existen</span>
            </label>
          </div>

          <div className="flex justify-end space-x-2">
            <button onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-50">Cancelar</button>
            <button onClick={handleSubmit} disabled={isSubmitting || !file} className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50">
              {isSubmitting ? 'Procesando...' : (dryRun ? 'Simular' : 'Importar')}
            </button>
          </div>

          {preview && (
            <div className="mt-4 border-t pt-4">
              <div className="text-sm">Creados: <strong>{preview.created}</strong> · Actualizados: <strong>{preview.updated}</strong> · Errores: <strong>{preview.errors.length}</strong></div>
              {preview.errors.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1 text-left">Fila</th>
                        <th className="px-2 py-1 text-left">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {preview.errors.map((err, idx) => (
                        <tr key={idx}>
                          <td className="px-2 py-1">{err.row}</td>
                          <td className="px-2 py-1">{err.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportProductosModal;


